import { useMemo } from 'react';

import { IBrand, useGetBrandsListQuery } from 'entities/Brand';
import { ESourceRelationStatuses, IOffer, ISourceRelation } from 'entities/Offer';
import { ESourceStatuses, ISource, useGetSourceListQuery } from 'entities/Source';
import { META_REQUEST_LIMIT } from 'features/Filters';

export const useMeta = () => {
	const { data: sources } = useGetSourceListQuery(`limit=${META_REQUEST_LIMIT}`);
	const activeSources = useMemo(
		() => sources?.results?.filter(({ status }) => status === ESourceStatuses.ACTIVE) || [],
		[sources],
	);

	const activeSourcesOptions = useMemo(
		() =>
			activeSources.map(({ name, uuid }) => ({
				value: uuid,
				label: name,
			})) || [],
		[activeSources],
	);

	const sourcesMap: Record<string, ISource> = useMemo(
		() =>
			sources?.results?.reduce(
				(acc, source) => ({
					...acc,
					[source.uuid]: source,
				}),
				{},
			) || {},
		[sources],
	);

	const { data: brands } = useGetBrandsListQuery(`limit=${META_REQUEST_LIMIT}`);

	const brandsMap: Record<string, IBrand> = useMemo(
		() =>
			brands?.results?.reduce(
				(acc, brand) => ({
					...acc,
					[brand.uuid]: brand,
				}),
				{},
			) || {},
		[brands],
	);

	const offers = useMemo(() => brands?.results?.flatMap(({ offers }) => offers) || [], [brands]);

	const offersMap: Record<string, IOffer> = useMemo(
		() =>
			offers.reduce(
				(acc, offer) => ({
					...acc,
					[offer.uuid]: offer,
				}),
				{},
			) || {},
		[offers],
	);

	const getSourceRelationsMap = (sourceRelations: ISourceRelation[]) =>
		sourceRelations.reduce(
			(acc, relation) => {
				return { ...acc, [relation.source.uuid]: relation };
			},
			{} as Record<string, ISourceRelation>,
		);

	const sourceCanJoin = (sourceUuid: string, sourceRelations: ISourceRelation[]) => {
		const sourceRelationsMap = getSourceRelationsMap(sourceRelations);

		return (
			!sourceRelationsMap[sourceUuid] ||
			(sourceRelationsMap[sourceUuid].status === ESourceRelationStatuses.REJECTED &&
				sourceRelationsMap[sourceUuid].rejected_count < 3)
		);
	};

	return {
		sourcesCount: sources?.count || 0,
		activeSourcesCount: activeSources.length,
		activeSourcesOptions,
		sourcesMap,
		brandsMap,
		offersMap,

		userCanJoinToOffer: (sourceRelations: ISourceRelation[]) =>
			activeSources.some((source) => sourceCanJoin(source.uuid, sourceRelations)),

		sourceCanJoin,
	};
};
